/**
 * **Are we interviewing where the priority ratings say we should?**
 *
 * Theresa, Oct 19 2026: *"I want the sections to disagree to show if we are acting against the
 * priority ratings. This is the best signal to show that we are e.g. interviewing too much for
 * roles with low priority and thereby block capacity for high prio roles."*
 *
 * The Cluster View carries two measures that can disagree — the hours the open roles book, and the
 * index built on what the team is actually doing. The disagreement is not noise to caption away:
 * it is the one thing on the tab that can say the week is going somewhere the ratings did not ask
 * for. This turns it into a figure.
 *
 * ⚠️ IT COMPARES SHARES, NOT HOURS, and that is what makes it honest with the data we have. The
 * effort model prices interviewing in HOURS per role per week; the warehouse counts INTERVIEWS.
 * There is no hours-per-interview figure anywhere at FINN, and inventing one would put a guess
 * underneath every number this produces. Two shares of their own company total need no such
 * constant:
 *
 *     expected share  the interviewing hours a cluster's own rating mix buys, over the company's
 *     actual share    the interviews that cluster actually ran last week, over the company's
 *
 * A cluster running 45% of the interviews while its ratings buy 20% of the interviewing is the
 * signal. **C-rated roles buy ZERO interviewing** (`SEED_EFFORT.C.interviewing === 0`) — TA owns
 * pipeline management on them and nothing else — so a cluster full of C roles that still runs
 * interviews shows up here by construction, which is exactly the case named above.
 *
 * ⚠️ WHAT IT DOES NOT SAY. A share is relative: a cluster can rise because another went quiet, and
 * "above its share" is not "working too hard". It says where the interviewing is going, against
 * where the ratings said it should. The UI states that rather than letting it read as a verdict on
 * a team.
 *
 * ⚠️ BOTH SHARES ARE TAKEN OVER THE SAME CLUSTERS, and getting that wrong was a real bug in the
 * first version (CodeRabbit, PR #637). It excluded a cluster with no interview count from the
 * ACTUAL denominator and left it in the EXPECTED one — so with Growth at 40 interviews and
 * Operations unreadable, Growth ran "100%" of an interviewing budget it bought half of, and was
 * reported as massively above its share when Operations may have run the other half. A share is
 * only a share of something; the something has to be the same on both sides.
 *
 * ⚠️ Both halves are judgement and both live here, not in the proxy — the same split the radar's
 * traffic lights take. The warehouse ships interview counts; what counts as "acting against the
 * ratings" is a statement about how FINN wants to hire and will be re-cut.
 */
import type { EffortModel, Rating } from '../types/rolePrioritisation';
import { RATINGS } from '../types/rolePrioritisation';
import type { ClusterStats } from './rolePrioritisation';

/**
 * Below this many interviews a week across the company, a share is one or two bookings moving and
 * the comparison says nothing. Same reasoning as the radar's `MIN_COHORT` and the offer trend's
 * floor: "we cannot say" beats a confident arrow on a cohort that cannot carry one.
 */
export const MIN_COMPANY_INTERVIEWS = 15;

/** And below this, one cluster's own share is noise even when the company total clears the floor. */
export const MIN_CLUSTER_INTERVIEWS = 3;

/**
 * Two clusters with counts, minimum. With one, both shares are 100% by construction and the
 * comparison carries no information at all — it would report "in line" whatever happened.
 */
export const MIN_CLUSTERS_COMPARED = 2;

/**
 * Percentage points of gap before it is called. Three clusters average 33 points each, so ten
 * points is roughly a third of a cluster's expected share — big enough not to fire on rounding,
 * small enough to catch a week that has genuinely drifted.
 */
export const GAP_POINTS = 10;

export type AlignmentVerdict = 'above' | 'below' | 'inline' | 'unknown';

export interface AlignmentRow {
  cluster: string;
  /** Interviewing hours this cluster's rating mix buys, per week. */
  expectedHours: number;
  expectedShare: number | null;
  interviews: number | null;
  actualShare: number | null;
  /** actual − expected, in percentage points. Positive = more interviewing than the ratings buy. */
  gapPoints: number | null;
  verdict: AlignmentVerdict;
  /** C-rated roles here. They buy no interviewing at all, which is why they are worth naming. */
  cRoles: number;
}

/**
 * WHY nothing could be judged, when nothing could.
 *
 * ⚠️ These are three different facts and the UI must not say the wrong one (CodeRabbit, PR #637).
 * "Too few interviews last week" sends a lead looking at their week; "only one cluster could be
 * read" sends them to the missing sheet row, which is the thing somebody can actually fix.
 */
export type NotJudgeable = 'thin-week' | 'one-cluster' | 'no-interviewing';

export interface Alignment {
  rows: AlignmentRow[];
  /** False when the week is too thin to read — the UI says so instead of drawing a conclusion. */
  judgeable: boolean;
  /** Set exactly when `judgeable` is false. */
  notJudgeable: NotJudgeable | null;
  /** Clusters whose interviews could not be read. Named, because they weaken every other share. */
  unreadable: string[];
  /** The cluster furthest ABOVE what its ratings buy, when there is one. */
  worst: AlignmentRow | null;
  companyInterviews: number;
}

/** The interviewing hours one cluster's open roles buy, at the agreed rate for their ratings. */
export function interviewingHoursFor(stats: ClusterStats, model: EffortModel): number {
  return RATINGS.reduce(
    (sum, r: Rating) => sum + (stats.roles[r] ?? 0) * (model[r]?.interviewing ?? 0),
    0
  );
}

export function priorityAlignment(
  stats: ClusterStats[],
  model: EffortModel,
  interviewsByCluster: Record<string, number | null>
): Alignment {
  const expected = stats.map((c) => ({ c, hours: interviewingHoursFor(c, model) }));

  /*
   * ⚠️ BOTH DENOMINATORS ARE THE CLUSTERS WE HAVE COUNTS FOR. Treating a missing read as zero
   * would inflate every other cluster's actual share; leaving the cluster in the EXPECTED total
   * while dropping it from the actual one does the same thing one step later, and is the bug this
   * comparison shipped with. One population, both sides.
   */
  const known = stats.filter((c) => typeof interviewsByCluster[c.key] === 'number');
  const knownKeys = new Set(known.map((c) => c.key));
  const unreadable = stats.filter((c) => !knownKeys.has(c.key)).map((c) => c.key);

  const companyInterviews = known.reduce((s, c) => s + (interviewsByCluster[c.key] as number), 0);
  const expectedTotal = expected
    .filter(({ c }) => knownKeys.has(c.key))
    .reduce((s, e) => s + e.hours, 0);

  /*
   * Ordered so the reason is the most ACTIONABLE one: a missing cluster is a sheet row somebody
   * can add, a thin week is just a quiet week, and no interviewing at all means the portfolio is
   * rated in a way that buys none.
   */
  const notJudgeable: NotJudgeable | null =
    known.length < MIN_CLUSTERS_COMPARED
      ? 'one-cluster'
      : expectedTotal <= 0
        ? 'no-interviewing'
        : companyInterviews < MIN_COMPANY_INTERVIEWS
          ? 'thin-week'
          : null;
  const judgeable = notJudgeable == null;

  const rows: AlignmentRow[] = expected.map(({ c, hours }) => {
    const interviews = typeof interviewsByCluster[c.key] === 'number' ? interviewsByCluster[c.key] : null;
    // Null for a cluster outside the compared set — it has no share of a total it is not in.
    const expectedShare = knownKeys.has(c.key) && expectedTotal > 0 ? hours / expectedTotal : null;
    const actualShare =
      interviews != null && companyInterviews > 0 ? interviews / companyInterviews : null;

    const gapPoints =
      expectedShare != null && actualShare != null
        ? Math.round((actualShare - expectedShare) * 1000) / 10
        : null;

    let verdict: AlignmentVerdict = 'unknown';
    if (judgeable && gapPoints != null && (interviews ?? 0) >= MIN_CLUSTER_INTERVIEWS) {
      verdict = gapPoints > GAP_POINTS ? 'above' : gapPoints < -GAP_POINTS ? 'below' : 'inline';
    }

    return {
      cluster: c.key,
      expectedHours: hours,
      expectedShare,
      interviews,
      actualShare,
      gapPoints,
      verdict,
      cRoles: c.roles.C ?? 0,
    };
  });

  const above = rows.filter((r) => r.verdict === 'above');
  const worst = above.length
    ? above.reduce((a, b) => ((b.gapPoints ?? 0) > (a.gapPoints ?? 0) ? b : a))
    : null;

  return { rows, judgeable, notJudgeable, worst, companyInterviews, unreadable };
}
